/**
 * Renders flight timeline + gantt + metrics from POST /v1/flights/timeline (or stored snapshot).
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import type {
  FlightMetrics,
  FlightTimelineBuildResponse,
  GanttBar,
  TimelineFlight,
  TimelinePlan,
} from '../../types/chat';
import { brandColors } from '../../theme/orchestrateTheme';
import { DEFAULT_CURRENCY_CODE, formatCurrency } from '../../utils/formatting';

/** The plan's currency in the user's locale (ORCH-191): symbol from the data, separators from the locale. */
function formatMoney(n: number, currency: string) {
  return formatCurrency(n, currency || DEFAULT_CURRENCY_CODE) || `${currency} ${n.toFixed(2)}`;
}

function FlightsTable({ flights, currency }: { flights: TimelineFlight[]; currency: string }) {
  return (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell>Flight</TableCell>
          <TableCell>Channel</TableCell>
          <TableCell>Start</TableCell>
          <TableCell>End</TableCell>
          <TableCell align="right">Net</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {flights.map((f) => (
          <TableRow key={f.id}>
            <TableCell>{f.name || f.id}</TableCell>
            <TableCell>{f.channel}</TableCell>
            <TableCell>{f.start_date}</TableCell>
            <TableCell>{f.end_date}</TableCell>
            <TableCell align="right">{formatMoney(f.net_cost, currency)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function GanttBlock({ bars }: { bars: GanttBar[] }) {
  const byRow = React.useMemo(() => {
    const m = new Map<string, GanttBar[]>();
    bars.forEach((b) => {
      m.set(b.row_key, [...(m.get(b.row_key) || []), b]);
    });
    return m;
  }, [bars]);

  return (
    <Box sx={{ mt: 2, overflowX: 'auto' }}>
      {Array.from(byRow.entries()).map(([rowKey, rowBars]) => (
        <Box key={rowKey} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {rowKey}
          </Typography>
          <Box
            sx={{
              position: 'relative',
              height: 36,
              bgcolor: 'grey.100',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
            }}
          >
            {rowBars.map((bar) => (
              <Box
                key={bar.id}
                title={`${bar.channel} ${bar.start_date}–${bar.end_date}`}
                sx={{
                  position: 'absolute',
                  left: `${bar.left_percent}%`,
                  width: `${Math.max(bar.width_percent, 0.5)}%`,
                  top: 6,
                  height: 24,
                  bgcolor: brandColors.teal,
                  opacity: 0.85,
                  borderRadius: 0.5,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  fontSize: 10,
                  px: 0.5,
                  lineHeight: '24px',
                  color: '#fff',
                }}
              >
                {bar.channel}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function MetricsChips({ metrics, currency }: { metrics: FlightMetrics; currency: string }) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
      <Chip size="small" label={`Flights: ${metrics.total_flights}`} />
      <Chip
        size="small"
        color="primary"
        variant="outlined"
        label={`Net: ${formatMoney(metrics.total_net_cost, currency)}`}
      />
      {metrics.total_budget != null ? (
        <Chip
          size="small"
          variant="outlined"
          label={`Budget: ${formatMoney(metrics.total_budget, currency)}`}
        />
      ) : null}
    </Stack>
  );
}

function SinglePlanView({
  timeline,
  gantt,
  metrics,
}: {
  timeline: TimelinePlan;
  gantt: FlightTimelineBuildResponse['gantt'];
  metrics: FlightMetrics | null | undefined;
}) {
  const currency = timeline.currency || 'USD';
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: brandColors.deepViolet }}>
        {timeline.plan_name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {timeline.start_date} → {timeline.end_date} · {timeline.country || '—'} ·{' '}
        {timeline.objective || '—'}
      </Typography>
      <FlightsTable flights={timeline.flights} currency={currency} />
      {gantt?.bars?.length ? <GanttBlock bars={gantt.bars} /> : null}
      {metrics ? <MetricsChips metrics={metrics} currency={currency} /> : null}
    </Box>
  );
}

export interface FlightTimelinePanelProps {
  data: FlightTimelineBuildResponse;
  onBack: () => void;
  onOpenMixer?: () => void;
  loading?: boolean;
}

const FlightTimelinePanel: React.FC<FlightTimelinePanelProps> = ({
  data,
  onBack,
  onOpenMixer,
  loading,
}) => {
  const [tab, setTab] = React.useState(0);
  const scenarios = data.scenarios?.filter(Boolean) ?? [];
  const singleScenario = scenarios.length === 1 ? scenarios[0] : null;
  const hasRenderableTimeline =
    Boolean(data.timeline) || scenarios.some((s) => s?.timeline != null);

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography>Loading flight plan…</Typography>
      </Paper>
    );
  }

  if (!data.show_timeline || !hasRenderableTimeline) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Alert severity="info">{data.reason || 'No flight timeline to display yet.'}</Alert>
        <Button sx={{ mt: 2 }} onClick={onBack}>
          Back to plan form
        </Button>
      </Paper>
    );
  }

  const header = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      gap={1}
    >
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        Media flight plan
      </Typography>
      <Stack direction="row" spacing={1}>
        {onOpenMixer ? (
          <Button variant="outlined" size="small" onClick={onOpenMixer}>
            Media mixer
          </Button>
        ) : null}
        <Button variant="text" size="small" onClick={onBack}>
          Back to plan form
        </Button>
      </Stack>
    </Stack>
  );

  if (singleScenario) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        {header}
        <Box sx={{ mt: 2 }}>
          <SinglePlanView
            timeline={singleScenario.timeline}
            gantt={singleScenario.gantt}
            metrics={singleScenario.metrics}
          />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      {header}

      {scenarios.length > 1 ? (
        <>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mt: 1, borderBottom: 1, borderColor: 'divider' }}
          >
            {scenarios.map((s, i) => (
              <Tab key={s.scenario_code || i} label={s.scenario_label || s.scenario_code} />
            ))}
          </Tabs>
          {scenarios.map((s, i) =>
            i === tab ? (
              <Box key={s.scenario_code} sx={{ mt: 2 }}>
                <SinglePlanView timeline={s.timeline} gantt={s.gantt} metrics={s.metrics} />
              </Box>
            ) : null
          )}
        </>
      ) : (
        <Box sx={{ mt: 2 }}>
          <SinglePlanView timeline={data.timeline!} gantt={data.gantt} metrics={data.metrics} />
        </Box>
      )}
    </Paper>
  );
};

export default FlightTimelinePanel;
